import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import "./index.scss";
import classNames from "classnames";
import { v4 as uuidv4 } from "uuid";
import {
  useToast,
  Box,
  Typography,
  Avatar,
  Icons,
  Tooltip,
  Button,
  InputArea,
  getColor,
} from "fds";
import i18n from "fds/src/locales/i18n";
import utilsCommon from "../../shared/utils/common";
import utilsDocs from "../../shared/utils/docs";
import { DocInfo, DocAIChatRequest } from "../../types/files";
import AIChatIconMenu from "./AIChatIconMenu";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import dayjs from "dayjs";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import { nanoid } from "@reduxjs/toolkit";
import AiChatSendIcon from "../../shared/icons/AiChatSendIcon";
import AiChatStopIcon from "../../shared/icons/AiChatStopIcon";

export interface MenuState {
  onClose?: () => void;
  width?: number | string;
  onDropFile?: any;
  receivedDragFiles?: DocInfo[];
  removeFile?: (syncId: string) => void;
}
interface ChatItem {
  type: "system" | "chat" | "bubble";
  sequnceNo: number;
  body?: any;
}
interface AIChatBubbleProps {
  type: "default" | "question";
  onClick?: () => void;
  classes?: string;
  children?: any;
}

const AIChatBubble: React.FC<AIChatBubbleProps> = ({
  type,
  onClick,
  classes,
  children,
}) => {
  return (
    <Box
      onClick={onClick}
      classes={classNames("aiChatBubble", classes, type)}
      style={{ cursor: onClick ? "pointer" : "" }}
    >
      {children}
    </Box>
  );
};

const AiChatPage: React.FC<MenuState> = ({
  receivedDragFiles,
  width,
  onDropFile,
  removeFile,
  onClose,
}) => {
  const toasts = useToast();
  const { t } = useTranslation();
  const {
    doAIDocChat,
    cancelAIDocChat,
    closeAIDocChat,
    checkAnswerAIDocChat,
    checkAIDocPermission,
  } = useDocumentStore();
  const element = document.getElementById("aiChat-body");
  const copy = require("clipboard-copy");
  const ref = useRef<any>();
  const [myInfo, changeMyInfo] = useState<LoginDto | null>(null);
  const [convoId, changeConvoId] = useState("");
  const [defaultQuestionList] = useState([
    t("file.aiChat.exQuestion1"),
    t("file.aiChat.exQuestion2"),
    t("file.aiChat.exQuestion3"),
  ]);
  const [newQuestion, changeNewQuestion] = useState("");
  const [chatList, changechatList] = useState<ChatItem[]>([]);
  const [mode] = useState(onClose ? "float" : "preview");
  const [files, changeFiles] = useState<DocInfo[]>();

  const sleep = (sec: number): any => {
    return new Promise((resolve) => setTimeout(resolve, sec * 1000));
  };
  const getAnswerConsecutively = async (
    chat: string,
    chatListSoFar: ChatItem[],
    targetIndex: number
  ): Promise<void> => {
    const element = document.getElementById("aiChat-body");

    let beforeHeight;
    let replaceChat = chat.replaceAll("<br/>", `\n`);
    for (let i = 1; i <= replaceChat.length; i++) {
      await sleep(0.0005);
      beforeHeight = element?.scrollHeight ?? 0;
      changechatList(
        chatListSoFar.map((item, index) => {
          if (index === targetIndex) {
            return {
              ...item,
              body: {
                ...item.body,
                respond: {
                  status: i === replaceChat.length ? "finished" : "answering",
                  answer: replaceChat.substring(0, i),
                },
              },
            };
          } else {
            return { ...item };
          }
        })
      );
      moveScrollToBottom(beforeHeight);
    }
  };
  const moveScrollToBottom = (beforeHeight: number): void => {
    const element = document.getElementById("aiChat-body");
    if (
      element &&
      element.scrollTop >=
        Math.floor(element.scrollHeight - element.offsetHeight) - 1
    ) {
      element.scrollTo(0, element.scrollHeight);
    }
  };
  const chatStatusArr = useRef<string[]>([]);
  const handleSendNewQuestion = async (question?: string): Promise<void> => {
    const targetSequence = chatStatusArr.current.length;
    if ((!question && !newQuestion) || !checkAnswerAIDocChat) return;
    const targetQuestion = question ?? newQuestion;
    if (!question) changeNewQuestion("");
    if (
      chatList.filter(
        (item) =>
          item.type === "chat" &&
          !["finished", "canceled", "answering", "error"].includes(
            item.body.respond.status
          )
      ).length > 0
    )
      return;
    changechatList([
      ...chatList,
      {
        type: "chat",
        sequnceNo: targetSequence,
        body: {
          request: targetQuestion,
          respond: {
            answer: "",
            status: "processing",
          },
        },
      },
    ]);
    const resp: any = await doAIDocChat({
      convoId: convoId,
      userChat: targetQuestion,
      fileList: files?.map((item) => {
        return {
          syncId: item.syncId,
          revisionNo: item.revisionNo,
          contentName: item.contentName,
          extension: item.contentName
            .toLowerCase()
            .substr(item.contentName.lastIndexOf(".") + 1),
        };
      }),
      sequenceNo: targetSequence,
      userId: myInfo?.userInfo.userId, // 채팅을 요청한 사용자의 ID
      language: i18n.language.includes("ko") ? "ko" : "en",
    });
    if (resp.payload && resp.payload.status !== "accepted") return;
    let status = false;
    chatStatusArr.current.push("processing");
    let count = 0;
    while (chatStatusArr.current[targetSequence] === "processing") {
      count++;
      const resp1: any = await checkAnswerAIDocChat({
        convoId,
        sequenceNo: targetSequence,
      });

      if (resp1.error || resp1?.payload?.status !== "processing") {
        status = true;
      }
      if (resp1?.payload?.status === "ok" && resp1.payload?.info?.answer) {
        chatStatusArr.current[targetSequence] = "answering";
        getAnswerConsecutively(
          resp1.payload.info.answer,
          [
            ...chatList,
            {
              type: "chat",
              sequnceNo: chatList.length,
              body: {
                request: targetQuestion,
                respond: {
                  status: "processing",
                  answer: "",
                },
              },
            },
          ],
          chatList.length
        );
      } else if (resp1.error?.name === "NO_CONTENT") {
        chatStatusArr.current[targetSequence] = "error";
        changechatList([
          ...chatList,
          {
            type: "chat",
            sequnceNo: chatList.length,
            body: {
              request: targetQuestion,
              respond: {
                status: "finished",
                answer: t("file.aiChat.alert.noContent"),
              },
            },
          },
        ]);
      } else if (resp1.error?.name === "RESOURCE_REQUIREMENT_UNMATCHED") {
        chatStatusArr.current[targetSequence] = "error";
        changechatList([
          ...chatList,
          {
            type: "chat",
            sequnceNo: chatList.length,
            body: {
              request: targetQuestion,
              respond: {
                status: "finished",
                answer: t("file.aiChat.alert.unmatched"),
              },
            },
          },
        ]);
      } else if (resp1.error?.name === "DUPLICATED") {
        chatStatusArr.current[targetSequence] = "error";
        changechatList([
          ...chatList,
          {
            type: "chat",
            sequnceNo: chatList.length,
            body: {
              request: targetQuestion,
              respond: {
                status: "finished",
                answer: t("file.aiChat.alert.duplicated"),
              },
            },
          },
        ]);
      } else if (resp1.error?.name === "REQUEST_CANCELLED_BY_USER") {
        return;
      } else if (resp1.error?.name === "CONTENT_LENGTH_EXCEED") {
        chatStatusArr.current[targetSequence] = "error";
        changechatList([
          ...chatList,
          {
            type: "chat",
            sequnceNo: chatList.length,
            body: {
              request: targetQuestion,
              respond: {
                status: "error",
                answer: "errrorrr",
              },
            },
          },
          {
            type: "system",
            sequnceNo: chatList.length,
            body: "CONTENT_LENGTH_EXCEED",
          },
        ]);
      } else if (count === 82 || resp1.error?.name === "RESPONSE_TIMEOUT") {
        chatStatusArr.current[targetSequence] = "error";
        changechatList([
          ...chatList,
          {
            type: "chat",
            sequnceNo: chatList.length,
            body: {
              request: targetQuestion,
              respond: {
                status: "error",
                answer: "errrorrr",
              },
            },
          },
          {
            type: "system",
            sequnceNo: chatList.length,
            body: "RESPONSE_TIMEOUT",
          },
        ]);
      } else if (count === 7) {
        changechatList([
          ...chatList,
          {
            type: "chat",
            sequnceNo: chatList.length,
            body: {
              request: targetQuestion,
              respond: {
                status: "processing",
                answer: "",
              },
            },
          },
          {
            type: "system",
            sequnceNo: chatList.length,
            body: "RESPONSE_DELAY",
          },
        ]);
      } else if (resp1 && resp1?.payload?.status !== "processing") {
        chatStatusArr.current[targetSequence] = "error";
        changechatList([
          ...chatList,
          {
            type: "chat",
            sequnceNo: chatList.length,
            body: {
              request: targetQuestion,
              respond: {
                status: "error",
                answer: "errrorrr",
              },
            },
          },
          {
            type: "system",
            sequnceNo: chatList.length,
            body: resp.error?.name,
          },
        ]);
      } else if (!resp1) {
        chatStatusArr.current[targetSequence] = "error";
      }
      await sleep(4);
    }
  };
  const isProgressing = (): boolean => {
    return (
      chatList.length > 0 &&
      chatList.filter(
        (chat) => chat && chat?.body?.respond?.status === "processing"
      ).length > 0
    );
  };
  const handleRemoveDuplicate = (files: DocInfo[]): DocInfo[] => {
    const returnSyncId = [] as string[];
    return files?.filter((item) => {
      const isDuplicate = returnSyncId.includes(item.syncId);
      if (!isDuplicate) {
        returnSyncId.push(item.syncId);
        return item;
      }
    });
  };

  useEffect(() => {
    const user = getUserInfoFromLocalStorage() as string | "";
    const loginDto: LoginDto = JSON.parse(user);
    changeMyInfo(loginDto);
    changeConvoId(`aiChat_${uuidv4()}_${dayjs().valueOf()}`);
  }, []);
  useEffect(() => {
    const aiChat = document.getElementById("aiChat");
    if (!aiChat) return;
    const aiChatClasses = document.getElementsByClassName("aiChat-textField");
    if (!aiChatClasses) return;
    const aiChatClass = aiChatClasses[0] as any as HTMLElement;
    aiChatClass.style.bottom =
      aiChat.scrollHeight <= 44 ? "16px" : `${aiChat.offsetHeight - 44 + 16}px`;
  }, [newQuestion]);
  useEffect(() => {
    element?.scrollTo(0, element?.scrollHeight);
  }, [chatList.length]);

  useEffect(() => {
    if (files && files?.length > 0) {
      if (chatList.length > 0) {
        changechatList([
          ...chatList,
          { type: "system", sequnceNo: chatList.length, body: "DOC_CHANGED" },
          { type: "bubble", sequnceNo: chatList.length },
        ]);
      } else {
        changechatList([
          ...chatList,
          { type: "bubble", sequnceNo: chatList.length },
        ]);
      }
    }
  }, [files]);
  const chatSuggestionBubbles = (): React.ReactNode => {
    return (
      <Box direction="column" width={"100%"}>
        <Typography type="body2_rg">
          {mode === "preview"
            ? t("file.aiChat.greetAndSuggest")
            : t("file.aiChat.suggestion")}
        </Typography>
        <Box classes="aiChat-bubbles">
          {files &&
            files?.length > 0 &&
            defaultQuestionList
              .filter(
                (question, index2) =>
                  (files && files?.length >= 2) || index2 < 2
              )
              .map((item, index) => (
                <AIChatBubble
                  type={"default"}
                  key={`chatBubble-${index}`}
                  onClick={() => handleSendNewQuestion(item)}
                  classes="mg mt-8"
                >
                  {item}
                </AIChatBubble>
              ))}
        </Box>
      </Box>
    );
  };

  const handleCancelChat = (): void => {
    let cancelIndex =
      chatList[chatList.length - 1].body === "RESPONSE_DELAY"
        ? chatList.length - 2
        : chatList.length - 1;
    chatStatusArr.current[chatStatusArr.current.length - 1] = "canceled";
    cancelAIDocChat &&
      cancelAIDocChat({
        convoId,
        sequenceNo: chatStatusArr.current.length - 1,
      });
    changechatList(
      chatList
        .filter((item1, index1) => index1 <= cancelIndex)
        .map((item: ChatItem, index) => {
          if (index === cancelIndex)
            return {
              ...item,
              body: {
                ...item.body,
                respond: {
                  status: "canceled",
                  answer: "",
                },
              },
            };
          return { ...item };
        })
    );
  };
  const chatSystem = (
    type:
      | "DOC_CHANGED"
      | "RESPONSE_TIMEOUT"
      | "CONTENT_LENGTH_EXCEED"
      | "RESPONSE_DELAY"
  ): React.ReactNode => {
    return (
      <Box width={"auto"} classes={classNames("aiChat-systemLog", type)}>
        {type === "DOC_CHANGED" ? (
          <>
            <Icons variant="line" label="info" stroke="#25c16f" />
            <Typography classes="mg ml-8" type="body2_rg">
              {t("file.aiChat.alert.docChanged")}
            </Typography>
          </>
        ) : (
          <>
            <Icons variant="line" label="info" stroke="#faa131" />
            <Typography classes="mg ml-8" type="body2_rg">
              {type === "RESPONSE_TIMEOUT"
                ? t("file.aiChat.alert.timeoutErr")
                : type === "CONTENT_LENGTH_EXCEED"
                ? t("file.aiChat.alert.tooManyText")
                : type === "RESPONSE_DELAY"
                ? t("file.aiChat.alert.timeDelay")
                : t("common.alert.serverError")}
            </Typography>
          </>
        )}
      </Box>
    );
  };
  useEffect(() => {
    handleOnDropFile();
  }, []);
  const handleOnDropFile = async (
    e?: React.DragEvent<HTMLDivElement>
  ): Promise<void> => {
    if (
      chatList.filter((chat) =>
        ["processing", "answering"].includes(chat.body?.respond?.status)
      ).length > 0
    ) {
      toasts.toastMsg(
        uuidv4(),
        t("file.aiChat.alert.waitUntilFinish"),
        "error"
      );
      return;
    }

    if (receivedDragFiles && receivedDragFiles?.length > 0) {
      if (
        receivedDragFiles?.filter(
          (item) =>
            !utilsCommon.checkExtensionAvailable(
              item.contentName,
              "aiChatExtensions"
            )
        ).length > 0
      ) {
        toasts.toastMsg(nanoid(), t("file.aiChat.alert.noSupportExt"), "error");
        return;
      }
      const resp: any = await checkAIDocPermission(
        receivedDragFiles.map((item) => item.syncId)
      );
      if (resp.error && resp.error.name === "DELETED") {
        toasts.toastMsg(uuidv4(), t("file.aiChat.alert.deleted"), "error");
        return;
      } else if (resp.error) {
        toasts.toastMsg(uuidv4(), t("file.aiChat.alert.noAuth"), "error");
        return;
      }
      if (receivedDragFiles?.length > 5) {
        toasts.toastMsg(
          nanoid(),
          t("file.aiChat.alert.overCountFile"),
          "error"
        );
        return;
      }
      if (
        receivedDragFiles?.filter(
          (item) =>
            !utilsCommon.checkExtensionAvailable(
              item.contentName,
              "aiChatExtensions"
            )
        ).length > 0
      ) {
        toasts.toastMsg(uuidv4(), t("file.aiChat.alert.noSupportExt"), "error");
        return;
      }
      let newFileList = handleRemoveDuplicate([
        ...receivedDragFiles,
        ...(files ?? []),
      ]);
      newFileList = newFileList.filter((item) =>
        utilsCommon.checkExtensionAvailable(
          item.contentName,
          "aiChatExtensions"
        )
      );

      if (newFileList?.length > 5) {
        toasts.toastMsg(
          nanoid(),
          t("file.aiChat.alert.overCountFile"),
          "error"
        );
        return;
      }
      if (JSON.stringify(files) !== JSON.stringify(newFileList))
        changeFiles([...newFileList]);
    }
    e && onDropFile(e, true);
  };
  const splitAndAddLineBreak = (instruction: string): string => {
    const splitInstruction = instruction.split(". ");
    return " " + splitInstruction.join(".\n ");
  };
  return (
    <Box
      direction="column"
      width={width || "100%"}
      height={"100%"}
      classes={`aiChat ${mode}`}
      onDrop={handleOnDropFile}
    >
      <Box
        classes={classNames(
          `aiChat-header`,
          element && element.scrollHeight > element.offsetHeight
            ? "scrollActive"
            : ""
        )}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box alignItems="center">
          <Icons variant="fill" label="aiChat" isOn />
          <Typography fontWeight={700} type="body2_rg" classes="mg ml-8">
            K-
          </Typography>
          <Typography fontWeight={500} type="body2_rg">
            {t("file.aiChat.title")}
          </Typography>
        </Box>
        {mode === "float" && (
          <Button
            onClick={() => {
              closeAIDocChat && closeAIDocChat(convoId);
              onClose && onClose();
            }}
            btnStyle="icon"
            leadingIcon={
              <Icons
                variant="line"
                label="closeLarge"
                size="24px"
                stroke="#B5BBC2"
              />
            }
          />
        )}
      </Box>
      {mode !== "preview" && files && files?.length > 0 && (
        <Box
          classes={classNames(
            "aiChat-receviedFiles",
            element && element.scrollHeight > element.offsetHeight
              ? "scrollActive"
              : ""
          )}
        >
          {files?.map((item) => (
            <Box
              classes="aiChat-receviedFile"
              key={`aiChat-receivedFile--${item.syncId}`}
            >
              <Box alignItems="center" width={"calc(100% - 24px)"}>
                {utilsDocs.renderDocIcon(item.contentName, 24)}
                <Tooltip
                  message={item.contentName}
                  classes="mg ml-4"
                  width="calc(100% - 28px)"
                >
                  <Typography
                    type="body2_rg"
                    lineHeight="20px"
                    fontWeight={500}
                    ellipsis
                    width="100%"
                  >
                    {item.contentName}
                  </Typography>
                </Tooltip>
              </Box>
              <Button
                btnStyle="table-icon"
                size="min"
                onClick={() => {
                  if (
                    chatList.filter((chat) =>
                      ["processing", "answering"].includes(
                        chat.body?.respond?.status
                      )
                    ).length > 0
                  ) {
                    toasts.toastMsg(
                      uuidv4(),
                      t("file.aiChat.alert.waitUntilFinish"),
                      "error"
                    );
                    return;
                  }
                  changeFiles(
                    files?.filter((item1) => item1.syncId !== item.syncId)
                  );
                  removeFile && removeFile(item.syncId);
                }}
                leadingIcon={
                  <Icons variant="line" label="closeLarge" size="16px" />
                }
              />
            </Box>
          ))}
        </Box>
      )}
      <div className="aiChat-body">
        <Box
          classes="aiChat-body--content"
          id="aiChat-body"
          height={
            i18n.language.includes("en")
              ? `calc(100% - ${
                  (document
                    .getElementById("aiChat-textField")
                    ?.getBoundingClientRect()?.height ?? 208) + 40
                }px)`
              : `calc(100% - ${
                  (document
                    .getElementById("aiChat-textField")
                    ?.getBoundingClientRect()?.height ?? 208) + 40
                }px)`
          }
        >
          {(!files || (files && files?.length === 0)) && (
            <Typography type="body2_rg">{t("file.aiChat.greeting")}</Typography>
          )}

          {files &&
            files?.length > 0 &&
            chatList.map((chat, index) =>
              chat.type === "bubble" ? (
                chatSuggestionBubbles()
              ) : chat.type === "system" ? (
                chatSystem(chat.body)
              ) : (
                <Box
                  classes="mg mt-24"
                  direction="column"
                  key={"aiChat-" + index}
                >
                  <Box>
                    <Avatar
                      name={
                        myInfo?.userInfo?.userName ??
                        myInfo?.userInfo?.userId ??
                        ""
                      }
                      uniqueCode={utilsCommon.getUserColor(
                        myInfo?.ecoUserInfo?.userType
                      )}
                    />
                    <AIChatBubble type={"question"} classes="mg ml-8">
                      {chat.body.request}
                    </AIChatBubble>
                  </Box>
                  {chat.body.respond.status !== "error" && (
                    <Box classes="mg mt-24">
                      <AIChatIconMenu
                        size={"md"}
                        borderRadius={"999px"}
                        color={"color"}
                      />
                      <Box classes="mg ml-8" width="calc(100% - 32px)">
                        {chat.body.respond.status === "processing" ? (
                          <Box classes="loading">
                            <span></span>
                            <span></span>
                            <span></span>
                          </Box>
                        ) : chat.body.respond.status === "answering" ? (
                          <Box>
                            <Typography
                              lineHeight="normal"
                              type="body2_rg"
                              whiteSpace={"break-spaces"}
                            >
                              {chat.body.respond.answer}
                              <Icons variant="fill" label="dot" />
                            </Typography>
                          </Box>
                        ) : chat.body.respond.status !== "canceled" ? (
                          <Box direction="column">
                            <Typography
                              lineHeight="normal"
                              type="body2_rg"
                              whiteSpace={"break-spaces"}
                            >
                              {chat.body.respond.answer}
                            </Typography>

                            <Tooltip
                              message={t("common.copy")}
                              classes="mg mt-10"
                            >
                              <Button
                                btnStyle="icon"
                                onClick={() => {
                                  copy(chat.body.respond.answer);
                                  toasts.toastMsg(
                                    uuidv4(),
                                    t("file.aiChat.alert.copySuccess"),
                                    "success"
                                  );
                                }}
                                leadingIcon={
                                  <Icons variant="line" label="copy" />
                                }
                              />
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography lineHeight="normal" type="body2_rg">
                            {t("file.aiChat.alert.userStopped")}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              )
            )}
        </Box>
        {files && files?.length > 0 ? (
          <Box
            ref={ref}
            classes="mg mt-auto"
            style={{ display: "inline-block", position: "relative" }}
          >
            <InputArea
              // padding={12}
              id="aiChat-textField"
              classes={classNames(
                "aiChat-textField",
                i18n.language.includes("en") ? "isEn" : "isKo"
              )}
              // fullWidth
              value={newQuestion}
              // id={"aiChat"}
              placeholder={t("file.aiChat.placeholder")}
              maxLength={250}
              // width="100%"
              isExtendable
              size="lg"
              trailingIcon={
                <Box
                  classes={classNames({ "cursor pointer": newQuestion !== "" })}
                  onClick={() =>
                    isProgressing()
                      ? handleCancelChat()
                      : handleSendNewQuestion()
                  }
                >
                  {isProgressing() ? (
                    <AiChatStopIcon />
                  ) : (
                    <AiChatSendIcon inactive={newQuestion === ""} />
                  )}
                </Box>
              }
              onChange={(e) => changeNewQuestion(e.target.value)}
            />
          </Box>
        ) : (
          <div className="aiChat-drag">
            <Typography
              type="body2_rg"
              exactColor={getColor("COLOR_TEXT_BRAND")}
              textAlign="justify"
              wordBreak="keep-all"
            >
              {splitAndAddLineBreak(t("file.aiChat.dropInstruction"))}
            </Typography>
          </div>
        )}
      </div>
    </Box>
  );
};

export default AiChatPage;
